/**
 * BrandingHeaderRow
 *
 * Renders a compact strip at the bottom of coloured gradient document headers.
 * Always shows a subtle "TeachBud" mark on the right; shows the school's own
 * branding (logo + text fields) on the left when the teacher has set any.
 *
 * Used by LessonPlanner, ActivitySheet, AssessmentGenerator, and
 * PresentationGenerator.
 */

import React from 'react';
import { hasBranding, type BrandingData } from './branding';

interface BrandingHeaderRowProps {
  branding: BrandingData;
  /** CSS color value for text — defaults to white/90 for coloured headers. */
  textColor?: string;
}

export function BrandingHeaderRow({ branding, textColor = 'rgba(255,255,255,0.90)' }: BrandingHeaderRowProps) {
  const { visibleFields: vf } = branding;

  const fields: string[] = [];
  if (vf.schoolName && branding.schoolName)     fields.push(branding.schoolName);
  if (vf.districtName && branding.districtName) fields.push(branding.districtName);
  if (vf.teacherName && branding.teacherName)   fields.push(branding.teacherName);
  if (vf.courseClass && branding.courseClass)   fields.push(branding.courseClass);
  if (vf.period && branding.period)             fields.push(`Period ${branding.period}`);
  if (vf.schoolYear && branding.schoolYear)     fields.push(branding.schoolYear);

  const showLogo   = vf.logo && !!branding.logoDataUrl;
  const hasSchool  = showLogo || fields.length > 0;

  return (
    <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-white/20 print:mt-2 print:pt-2">

      {/* Left — school branding (only when present) */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {showLogo && (
          <img
            src={branding.logoDataUrl}
            alt="School logo"
            className="h-7 w-auto rounded object-contain flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)', padding: '2px' }}
          />
        )}
        {hasSchool && (
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 min-w-0">
            {fields.map((f, i) => (
              <span key={i} className="text-xs font-medium truncate" style={{ color: textColor }}>
                {f}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Right — always-present TeachBud mark */}
      <div className="flex items-center gap-1 flex-shrink-0 select-none">
        <span
          className="text-[9px] font-bold tracking-[0.18em] uppercase"
          style={{ color: 'rgba(255,255,255,0.50)' }}
        >
          TeachBud
        </span>
      </div>
    </div>
  );
}
